/** The tmux view's last session list, queue and repo list, kept in this
 *  browser so the view draws at once on a slow connection while the real reads
 *  are on their way — as lib/gitGraphCache.ts does for the git panel. Only ever
 *  a first paint: nothing acts on it until a real read has replaced it. Per
 *  reader, so dropped on sign-out. */
import type { QueuedClaude, TmuxSession } from "@shared/tmux.js";
import type { RepoInfo } from "@shared/repoSettings.js";

interface Kept {
  sessions: TmuxSession[];
  queue: QueuedClaude[];
  repos: RepoInfo[];
}

const PREFIX = "todos.tmux:";

export function readKept<K extends keyof Kept>(key: K): Kept[K] | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? (v as Kept[K]) : null;
  } catch {
    return null;
  }
}

export function keep<K extends keyof Kept>(key: K, value: Kept[K]): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage blocked or full: the next visit just waits for the read.
  }
}

export function clearKept(): void {
  for (const key of ["sessions", "queue", "repos"] satisfies (keyof Kept)[]) {
    try {
      localStorage.removeItem(PREFIX + key);
    } catch {
      // Nothing was kept.
    }
  }
}
